// Haftanın Temsilcisi · 24 saat — Genel Bakış's live board (Task Board #292 Vegas / #293 Prime,
// asya 2026-10-28, "like in the pic": call-agent-admin's weekly report card).
//
// The rows, their order and the weights are call-agent-admin's (its agent-ranking.ts, relayed by
// /api/reports/agent-ranking) — nothing is scored here. The subtitle is built from the `formula`
// the rows came with, so a weight change cannot leave this screen quoting the old ones.
//
// LIVE: re-read every POLL_MS while the tab is visible. A failed poll keeps the rows already on
// screen and says so (the server does the same with `stale`); a period/brand change starts empty,
// never under the previous selection's rows.
//
// The agent sees the same board on Gelişimim (Task Board #298 Vegas / #299 Prime, asya 2026-10-30
// "i want call agents to see this format"): the server pins it to their brand, strips the raw
// inputs and flags their own row (`self`), which gets a "Sen" pill.

import { useEffect, useState } from 'react';
import * as api from '../api';
import type { AgentRanking, AgentRankingBrand, AgentRankingRow } from '../types';
import { Callout, Empty, Loading, Note, Panel, Pill } from '../ui';

const POLL_MS = 60_000;

const fmtPts = (v: number) => v.toFixed(2).replace('.', ',');
const fmtPct = (v: number) => '%' + v.toFixed(1).replace('.', ',');
const fmtTl = (v: number) => Math.round(v).toLocaleString('tr-TR') + ' TL';

/** SKOR bands — call-agent-admin's `finalScoreColor` (owner 2026-10-22): ≥ 8 green, ≥ 7 amber, else red. */
const scoreColor = (s: number) => (s >= 8 ? 'var(--good)' : s >= 7 ? 'var(--warn)' : 'var(--bad)');

function useLiveRanking(period: string, brand: string) {
  const [data, setData] = useState<AgentRanking | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError('');
    const load = () => api.getAgentRanking(period || undefined, brand || undefined)
      .then((v) => { if (!cancelled) { setData(v); setError(''); } })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof api.ApiError && err.status === 401) return;
        setError(err instanceof api.ApiError ? err.message : 'Veri alınamadı.');
      });
    load();
    const timer = window.setInterval(() => { if (!document.hidden) load(); }, POLL_MS);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [period, brand]);
  return { data, error };
}

export default function AgentOfWeek({ period, brand, forAgent = false }: { period: string; brand: string; forAgent?: boolean }) {
  const { data, error } = useLiveRanking(period, brand);

  if (!data) {
    return error ? <Note error>{error}</Note> : <Panel icon="emoji_events" title="Haftanın Temsilcisi · 24 saat"><Loading /></Panel>;
  }
  // The call-agent-admin link is off on this box — nothing to show, nothing broken.
  if (!data.configured || !data.brands.length) return null;

  const many = data.brands.length > 1;
  return (
    <>
      {error ? <Note error>{`Sıralama yenilenemedi: ${error}`}</Note> : null}
      {data.brands.map((b) => <BrandBoard key={b.brand} b={b} weekLabel={data.week.label} showBrand={many} forAgent={forAgent} />)}
    </>
  );
}

function BrandBoard({ b, weekLabel, showBrand, forAgent }: { b: AgentRankingBrand; weekLabel: string; showBrand: boolean; forAgent: boolean }) {
  const f = b.formula;
  const subtitle = f
    ? `QMS %${Math.round(f.qmsWeight * 10)} · Başarı oranı %${Math.round(f.successWeight * 10)} · Yatırım %${Math.round(f.depositMaxPoints * 10)} · Kritik hata −${String(f.criticalPenalty).replace('.', ',')}`
    : weekLabel;
  const right = (
    <span className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
      {showBrand ? <Pill tone="neu">{b.brandName}</Pill> : null}
      <Pill tone={b.stale || b.error ? 'warn' : 'good'}>{b.stale || b.error ? 'Bayat' : 'Canlı'}</Pill>
    </span>
  );
  const rows = b.rows ?? [];

  return (
    <Panel
      icon="emoji_events"
      title="Haftanın Temsilcisi · 24 saat"
      hint={subtitle}
      right={right}
      footer={(
        <div className="panel-foot" style={{ fontSize: 11.5, color: 'var(--faint)', lineHeight: 1.5 }}>
          <span>{weekLabel}</span>
          <span>{forAgent
            ? 'Ekibinin bu haftaki sıralaması; her dakika yenilenir. Değerlendirmesi olmayan temsilci 0 QMS puanı alır.'
            : 'Sıralama ve puanlar call-agent-admin Haftalık Rapor\'daki Haftanın Temsilcisi kartının aynısıdır; her dakika yenilenir. Değerlendirmesi olmayan temsilci 0 QMS puanı alır.'}</span>
        </div>
      )}
    >
      {b.error ? (
        <div className="panel-body"><Note error>{`Sıralama şu an okunamadı (${b.error.kind}) — her dakika yeniden deneniyor.`}</Note></div>
      ) : (
        <>
          {b.stale ? (
            <div className="panel-body tight"><Callout tone="warn" icon="history">Son yenileme başarısız — gösterilen sıralama son başarılı okumadan.</Callout></div>
          ) : null}
          {b.qms && !b.qms.available ? (
            <div className="panel-body tight"><Callout tone="bad" icon="warning">QMS puanları okunamadı — herkes 0 QMS puanıyla sıralandı; bu gerçek sıralama değil.</Callout></div>
          ) : null}
          {rows.length === 0 ? <Empty>Bu hafta henüz sıralanacak temsilci yok.</Empty> : <RankTable rows={rows} />}
        </>
      )}
    </Panel>
  );
}

function RankTable({ rows }: { rows: AgentRankingRow[] }) {
  return (
    <div className="tbl-wrap" style={{ maxHeight: 420, overflowY: 'auto' }}>
      <table>
        <thead>
          <tr>
            <th>Sıra</th><th>Temsilci</th>
            <th className="r">QMS puanı</th><th className="r">Başarı puanı</th><th className="r">Yatırım puanı</th>
            <th className="r">Kritik ceza</th><th className="r">Skor</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} style={r.rank === 1 ? { background: 'var(--accent-soft)' } : undefined}>
              <td><span className={`rank${r.rank <= 3 ? ' top' : ''}`}>{r.rank + '.'}</span></td>
              <td className="strong" style={{ fontWeight: r.rank === 1 || r.self ? 700 : 600 }}>
                {r.name}
                {r.self ? <>{' '}<Pill tone="accent">Sen</Pill></> : null}
              </td>
              <td className="r tnum" style={{ color: r.qmsScore == null ? 'var(--faint)' : 'var(--text)' }}
                title={r.qmsScore == null ? 'Bu hafta değerlendirme yok' : `${r.qmsEvalCount} değerlendirme`}>
                {fmtPts(r.qmsPoints)}
              </td>
              <td className="r tnum" style={{ color: 'var(--text)' }} title={r.successRatePct == null ? undefined : fmtPct(r.successRatePct)}>{fmtPts(r.successPoints)}</td>
              <td className="r tnum" style={{ color: 'var(--text)' }} title={r.depositAmount == null ? undefined : fmtTl(r.depositAmount)}>{fmtPts(r.depositPoints)}</td>
              {/* 0 critical mistakes is a real answer → faint 0,00 (not blank); the penalty carries its minus. */}
              <td className="r tnum" style={{ color: r.criticalPenalty > 0 ? 'var(--bad)' : 'var(--faint)' }} title={`${r.criticalCount} kritik`}>
                {r.criticalPenalty > 0 ? '−' + fmtPts(r.criticalPenalty) : fmtPts(0)}
              </td>
              <td className="r tnum" style={{ fontWeight: 700, color: scoreColor(r.score10) }}>{fmtPts(r.score10)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
